import React, { useMemo, useState } from "react";
import { usePermissions } from "../../context/PermissionContext";
import FormsBuildTab from "./tabs/FormsBuildTab";
import FormsReviewReportsTab from "./tabs/FormsReviewReportsTab";
import AdminFormAssignmentsPage from "../admins/AdminFormAssignmentsPage";
import FormResponsesPage from "./FormResponsesPage";
import "./FormsWorkspacePage.css";

// Dynamic Forms visibility audit, §7: this page is now also mounted at
// /<role>/forms (routes/DynamicRoutes.jsx) for every non-admin role, not
// just /admin/forms -- "Manage Form Types" must follow the SAME role
// prefix rather than a hardcoded /admin/ path.
function useRolePrefix() {
  const path = window.location.pathname;
  const match = path.match(/^\/([^/]+)\//);
  return match ? match[1] : "admin";
}

// Consolidates the previously-fragmented Forms sidebar (Form Types, Forms,
// Assign Form, Forms Assigned By Me, Review Forms, Form Review Queue, My
// Form Results, Form Execution Reports -- 8 separate pages) into ONE
// workspace with 3 tabs: Build / Assign & Track / Review & Reports. Every
// tab reuses the exact existing pages/components/hooks/APIs for its data
// and business logic -- this file only owns tab layout, permission-based
// visibility/default selection, and URL (?tab=&view=) persistence.
//
// Tab/sub-tab state is read from and written back to the URL via plain
// URLSearchParams/history.replaceState rather than react-router-dom's
// hooks -- react-router-dom resolves fine at runtime (webpack) but its
// installed package.json "main" field points at a missing dist file that
// breaks Jest's resolver (a pre-existing environment defect, unrelated to
// this feature); every other Forms page built this session avoids the
// same trap the same way.
const TAB_DEFS = [
  {
    key: "build",
    label: "Build",
    icon: "fa-solid fa-pen-ruler",
    isVisible: (can, canAny) => canAny([
      { moduleKey: "form_types", actionKey: "view" },
      { moduleKey: "forms", actionKey: "view" },
      { moduleKey: "forms", actionKey: "create" },
    ]),
  },
  {
    key: "assign",
    label: "Assign & Track",
    icon: "fa-solid fa-paper-plane",
    isVisible: (can, canAny) => canAny([
      { moduleKey: "form_assignments", actionKey: "create" },
      { moduleKey: "form_assignments", actionKey: "view" },
      { moduleKey: "form_assignments", actionKey: "view_all" },
      { moduleKey: "form_assignments", actionKey: "manage_all" },
    ]),
  },
  {
    key: "review",
    label: "Review & Reports",
    icon: "fa-solid fa-clipboard-check",
    isVisible: (can, canAny) => canAny([
      { moduleKey: "assigned_forms", actionKey: "view_own" },
      { moduleKey: "form_reviews", actionKey: "view_review_queue" },
      { moduleKey: "form_results", actionKey: "view_own_result" },
      { moduleKey: "form_results", actionKey: "view_result" },
      { moduleKey: "form_execution_reports", actionKey: "view_own" },
      { moduleKey: "form_execution_reports", actionKey: "view" },
    ]),
  },
];

function readParam(name) {
  try {
    return new URLSearchParams(window.location.search).get(name) || "";
  } catch {
    return "";
  }
}

function writeTabParam(tabKey) {
  try {
    const params = new URLSearchParams(window.location.search);
    params.set("tab", tabKey);
    params.delete("view");
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  } catch {
    // no-op -- URL persistence is a convenience, not a requirement
  }
}

export default function FormsWorkspacePage() {
  const { can, canAny, loading: permissionsLoading } = usePermissions();
  const rolePrefix = useRolePrefix();

  const visibleTabs = useMemo(
    () => TAB_DEFS.filter((t) => t.isVisible(can, canAny)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissionsLoading]
  );

  const [activeTab, setActiveTabState] = useState(() => {
    const requested = readParam("tab");
    return requested || "build";
  });

  if (permissionsLoading) {
    return (
      <div className="unit-page forms-workspace-theme">
        <div className="unit-loader"><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i></div>
      </div>
    );
  }

  // Manager dashboard's "View Team Forms" (?only=team-responses): a focused
  // view with just the team members' Responses table -- no Build / Assign &
  // Track / Review tabs, no sub-tab chips. Same page/API as the Responses
  // sub-tab, gated on the same reports:view_form_results permission.
  if (readParam("only") === "team-responses" && can("reports", "view_form_results")) {
    // The page owns its own header card + frame (FormResponsesPage teamOnly),
    // so only the theme scope (global-button fix, tokens) is applied here.
    return (
      <div className="forms-workspace-theme">
        <FormResponsesPage embedded teamOnly />
      </div>
    );
  }

  if (!visibleTabs.length) {
    return (
      <div className="unit-page forms-workspace-theme">
        <div className="unit-empty">
          <div className="unit-empty-icon"><i className="fa-solid fa-lock" aria-hidden="true"></i></div>
          <h5>Access denied</h5>
          <p>You do not have permission to access any part of the Forms workspace.</p>
        </div>
      </div>
    );
  }

  const resolvedTab = visibleTabs.some((t) => t.key === activeTab) ? activeTab : visibleTabs[0].key;

  const selectTab = (key) => {
    setActiveTabState(key);
    writeTabParam(key);
  };

  const initialView = readParam("view");
  const initialAssignTab = initialView === "assigned-by-me" ? "ASSIGNED_BY_ME" : undefined;

  return (
    <div className="unit-page forms-workspace-theme">
      <div className="forms-ws-header">
        <div>
          <h1>Forms Workspace</h1>
          <p>Create, assign, review and track forms from one place.</p>
        </div>
        <div className="forms-ws-header-actions">
          {/* Decorative only -- no notification system exists behind this
              yet, so it's non-interactive rather than a button that looks
              actionable but does nothing. */}
          <span className="forms-ws-icon-btn" aria-hidden="true" title="Notifications (not yet available)">
            <i className="fa-regular fa-bell"></i>
          </span>
          <button
            type="button"
            className="forms-ws-icon-btn"
            aria-label="Manage Form Types"
            title="Manage Form Types"
            onClick={() => window.location.assign(`/${rolePrefix}/form-types`)}
          >
            <i className="fa-solid fa-gear"></i>
          </button>
        </div>
      </div>

      <div className="forms-ws-tabs" role="tablist" aria-label="Forms workspace sections">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={resolvedTab === t.key}
            className={`forms-ws-tab ${resolvedTab === t.key ? "active" : ""}`}
            onClick={() => selectTab(t.key)}
          >
            <i className={t.icon} aria-hidden="true"></i>
            {t.label}
          </button>
        ))}
      </div>

      <div className="forms-ws-tab-panel">
        {resolvedTab === "build" && <FormsBuildTab />}
        {resolvedTab === "assign" && <AdminFormAssignmentsPage initialAssignTab={initialAssignTab} embedded />}
        {resolvedTab === "review" && <FormsReviewReportsTab initialView={initialView} />}
      </div>
    </div>
  );
}
